import React, { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormHelperText,
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  IconButton,
  Divider,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import AddIcon from '@material-ui/icons/Add';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

const AWS_REGIONS = [
  'eu-west-1',
  'us-east-1',
  'ap-south-1',
  'us-west-2',
  'eu-central-1',
];

const ENVIRONMENTS = ['sbx', 'dev', 'qa', 'uat', 'prd'];

interface Region {
  region: string;
  region_short_name: string;
  aws_vpc_id: string;
  tfstate_bucket: string;
  aws_acm_arn: string;
  logging_bucket: string;
  [key: string]: string;
}

export interface EnvironmentConfig {
  environment: string;
  aws_account_id: string;
  allowed_aws_regions: string[];
  default_aws_region: string;
  dns_subdomain: string;
  aws_hosted_zone_id: string;
  admin_role_arn: string;
  aws_global_acm_arn: string;
  auth0_enabled?: boolean;
  auth0_client_id?: string;
  auth0_domain?: string;
  auth0_client_secret?: string;
  auth0_scope?: string;
  regions: Region[];
  [key: string]: any;
}

export const ProjectEnvConfig = (
  props: FieldExtensionComponentProps<EnvironmentConfig[]>,
) => {
  const { onChange, formData, required, rawErrors } = props;
  // Blank environment template
      const blank: EnvironmentConfig = {
        environment: '',
        aws_account_id: '',
        allowed_aws_regions: [],
        default_aws_region: '',
        dns_subdomain: '',
        aws_hosted_zone_id: '',
        admin_role_arn: '',
        aws_global_acm_arn: '',
        auth0_enabled: false,
        auth0_client_id: '',
        auth0_domain: '',
        auth0_client_secret: '',
        auth0_scope: '',
        regions: [],
      };

  const [loading, setLoading] = useState(false);
  // Initialize local state from form value or a single blank entry
  const [envConfigs, setEnvConfigs] = useState<EnvironmentConfig[]>(
    () => (Array.isArray(formData) && formData.length > 0 ? formData : [blank]),
  );
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});

  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  // Loading and error state per environment index
  const [envLoading, setEnvLoading] = useState<{ [key: number]: boolean }>({});
  const [envError, setEnvError] = useState<{ [key: number]: string | null }>({});
  // Store fetched options per environment
  const [envOptions, setEnvOptions] = useState<{ 
    [key: number]: {
      hostedZones: Array<{id: string; name: string}>;
      adminRoles: Array<{arn: string; name: string}>;
      globalAcmCerts: Array<{arn: string; domain: string}>;
    }
  }>({});
  // Store fetched region-specific options per environment per region
  const [regionOptions, setRegionOptions] = useState<{
    [envIdx: number]: {
      [region: string]: {
        vpcs: Array<{id: string; name: string; cidr: string}>;
        buckets: Array<{name: string}>;
        regionalAcmCerts: Array<{arn: string; domain: string}>;
      }
    }
  }>({});
  const [regionLoading, setRegionLoading] = useState<{ [key: string]: boolean }>({});

  // Fetch environment defaults when AWS Account ID loses focus
  const handleAccountBlur = async (idx: number, accountId: string) => {
    if (!accountId) return;
    setEnvLoading({ ...envLoading, [idx]: true });
    try {
      const base = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${base}/aws/env-details`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ accountId }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      
      // Store the fetched options
      setEnvOptions({
        ...envOptions,
        [idx]: {
          hostedZones: data.hostedZones || [],
          adminRoles: data.adminRoles || [],
          globalAcmCerts: data.globalAcmCerts || [],
        }
      });
      
      setEnvError({ ...envError, [idx]: null });
    } catch (e: any) {
      // On error, ensure empty options are set so fields remain editable
      setEnvOptions({
        ...envOptions,
        [idx]: {
          hostedZones: [],
          adminRoles: [],
          globalAcmCerts: [],
        }
      });
      setEnvError({ ...envError, [idx]: `Failed to fetch AWS details: ${e.message}. Please enter values manually.` });
    } finally {
      setEnvLoading(prev => ({ ...prev, [idx]: false }));
    }
  };

  useEffect(() => {
    discoveryApi.getBaseUrl('scaffolder').then(() => setLoading(false));
  }, [discoveryApi]);

  // Add effect to sync envConfigs from form value
  useEffect(() => {
    if (Array.isArray(formData) && JSON.stringify(formData) !== JSON.stringify(envConfigs)) {
      setEnvConfigs(formData);
    }
  }, [formData]);

  // If external clear, propagate existing envConfigs
  useEffect(() => {
    if (Array.isArray(formData) && formData.length === 0 && envConfigs.length > 0) {
      onChange(envConfigs);
    }
  }, [formData, envConfigs]);

  const updateFormState = (updated: EnvironmentConfig[]) => {
    setEnvConfigs(updated);
    onChange(updated);
  };

  const handleAddEnvironment = () =>
    updateFormState([
      ...envConfigs,
      {
        environment: '',
        aws_account_id: '',
        allowed_aws_regions: [],
        default_aws_region: '',
        dns_subdomain: '',
        aws_hosted_zone_id: '',
        admin_role_arn: '',
        aws_global_acm_arn: '',
        auth0_enabled: false,
        auth0_client_id: '',
        auth0_domain: '',
        auth0_client_secret: '',
        auth0_scope: '',
        regions: [],
      },
    ]);

  const handleRemoveEnvironment = (idx: number) =>
    updateFormState(envConfigs.filter((_, i) => i !== idx));

  const handleEnvFieldChange = (
    idx: number,
    field: keyof EnvironmentConfig,
    val: any,
  ) =>
    updateFormState(
      envConfigs.map((e, i) =>
        i === idx
          ? {
              ...e,
              [field]: val,
            }
          : e,
      ),
    );

  // Fetch region-specific details
  const handleRegionDetailsFetch = async (envIdx: number, region: string, accountId: string) => {
    if (!accountId || !region) return;
    const key = `${envIdx}-${region}`;
    setRegionLoading({ ...regionLoading, [key]: true });
    try {
      const base = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${base}/aws/region-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ accountId, region }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      
      setRegionOptions({
        ...regionOptions,
        [envIdx]: {
          ...(regionOptions[envIdx] || {}),
          [region]: {
            vpcs: data.vpcs || [],
            buckets: data.buckets || [],
            regionalAcmCerts: data.regionalAcmCerts || [],
          },
        },
      });
    } catch (e: any) {
      console.error(`Failed to fetch region details for ${region}:`, e);
    } finally {
      setRegionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAllowedRegionsChange = (
    idx: number,
    selections: string[],
  ) => {
    const details = selections.map(region => ({
      region,
      region_short_name: generateRegionShortName(region),
      aws_vpc_id: '',
      tfstate_bucket: '',
      aws_acm_arn: '',
      logging_bucket: '',
    }));
    updateFormState(
      envConfigs.map((e, i) =>
        i === idx
          ? {
              ...e,
              allowed_aws_regions: selections,
              regions: details,
              default_aws_region: selections[0] || '',
            }
          : e,
      ),
    );
    setTouchedFields({
      ...touchedFields,
      [`${idx}-allowed_aws_regions`]: true,
    });
    
    // Fetch region details for newly selected regions
    const accountId = envConfigs[idx]?.aws_account_id;
    if (accountId) {
      selections.forEach(region => {
        handleRegionDetailsFetch(idx, region, accountId);
      });
    }
  };

  const handleDefaultRegionChange = (idx: number, val: string) => {
    handleEnvFieldChange(idx, 'default_aws_region', val);
    setTouchedFields({
      ...touchedFields,
      [`${idx}-default_aws_region`]: true,
    });
  };

  const handleRegionFieldChange = (
    envIndex: number,
    regionIndex: number,
    field: keyof Region,
    val: any,
  ) =>
    updateFormState(
      envConfigs.map((env, i) =>
        i !== envIndex
          ? env
          : {
              ...env,
              regions: env.regions.map((r, j) =>
                j === regionIndex
                  ? {
                      ...r,
                      [field]: val,
                    }
                  : r,
              ),
            },
      ),
    );

  const generateRegionShortName = (region: string) => {
    const parts = region.split('-');
    if (parts.length < 2) {
      return region;
    }
    const prefix = parts[0].slice(0, 2);
    const middle = parts
      .slice(1, -1)
      .map(p => p[0])
      .join('');
    const suffix = parts[parts.length - 1];
    return `${prefix}${middle}${suffix}`;
  };

  if (loading) {
    return (
      <FormControl fullWidth>
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress />
        </Box>
      </FormControl>
    );
  }
  // Removed error state as it's unused
  if (rawErrors?.length) {
    return (
      <FormControl fullWidth error>
        <FormHelperText>{rawErrors[0]}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length}>
      <Typography variant="h6" gutterBottom>
        Configure Environments
      </Typography>
      {envConfigs.length === 0 ? (
        <Box p={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddEnvironment}
          >
            Add Environment
          </Button>
        </Box>
      ) : (
        <>  {envConfigs.map((env, idx) => {
            // Prepare available environment options
            const used = envConfigs
              .map((e, i) => (i !== idx ? e.environment : null))
              .filter(Boolean as any) as string[];
            const options = ENVIRONMENTS.filter(e => !used.includes(e));

            return (
              <Paper
                key={idx}
                elevation={2}
                style={{ padding: 16, marginBottom: 24, borderLeft: '4px solid #2e77d0' }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                      <TextField
                        select
                        fullWidth
                        required
                        label="Environment"
                        value={env.environment}
                        onChange={e => handleEnvFieldChange(idx, 'environment', e.target.value)}
                        margin="dense"
                        variant="outlined"
                      >
                        <MenuItem key="" value="">
                          Select environment...
                        </MenuItem>
                        {options.map(opt => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton onClick={() => handleRemoveEnvironment(idx)} size="small">
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Box>
                  </Grid>

                  {/* AWS Account ID */}
                  <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                      label="AWS Account ID"
                      value={env.aws_account_id}
                      onChange={e => handleEnvFieldChange(idx, 'aws_account_id', e.target.value)}
                      onBlur={() => handleAccountBlur(idx, env.aws_account_id)}
                      InputProps={{
                        endAdornment: envLoading[idx] ? <CircularProgress size={20} /> : null,
                      }}
                      margin="dense"
                      variant="outlined"
                    />
                    {envError[idx] && (
                      <FormHelperText error>{envError[idx]}</FormHelperText>
                    )}
                  </Grid>

                  {/* DNS Subdomain - Autocomplete */}
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      forcePopupIcon
                      options={envOptions[idx]?.hostedZones || []}
                      value={env.dns_subdomain}
                      onChange={(_, val) => {
                        const domainName = typeof val === 'object' && val ? val.name : (typeof val === 'string' ? val : '');
                        const zoneId = typeof val === 'object' && val ? val.id : '';
                        handleEnvFieldChange(idx, 'dns_subdomain', domainName);
                        if (zoneId) {
                          handleEnvFieldChange(idx, 'aws_hosted_zone_id', zoneId);
                        }
                      }}
                      onInputChange={(_, newValue, reason) => {
                        // Only update on blur or clear, not on every keystroke
                        if (reason === 'reset' || reason === 'clear') {
                          handleEnvFieldChange(idx, 'dns_subdomain', newValue);
                        }
                      }}
                      onBlur={(e) => {
                        // Update on blur to capture manual typing
                        const target = e.target as HTMLInputElement;
                        if (target.value !== env.dns_subdomain) {
                          handleEnvFieldChange(idx, 'dns_subdomain', target.value);
                        }
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.name;
                      }}
                      renderOption={(option) => (
                        <div>
                          <div style={{ fontWeight: 500 }}>{option.name}</div>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>Zone ID: {option.id}</div>
                        </div>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          required
                          label="DNS Subdomain"
                          margin="dense"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>

                  {/* AWS Hosted Zone ID - Autocomplete */}
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      forcePopupIcon
                      options={envOptions[idx]?.hostedZones || []}
                      value={env.aws_hosted_zone_id}
                      onChange={(_, val) => {
                        const zoneId = typeof val === 'object' && val ? val.id : (typeof val === 'string' ? val : '');
                        handleEnvFieldChange(idx, 'aws_hosted_zone_id', zoneId);
                      }}
                      onInputChange={(_, newValue, reason) => {
                        // Only update on blur or clear, not on every keystroke
                        if (reason === 'reset' || reason === 'clear') {
                          handleEnvFieldChange(idx, 'aws_hosted_zone_id', newValue);
                        }
                      }}
                      onBlur={(e) => {
                        // Update on blur to capture manual typing
                        const target = e.target as HTMLInputElement;
                        if (target.value !== env.aws_hosted_zone_id) {
                          handleEnvFieldChange(idx, 'aws_hosted_zone_id', target.value);
                        }
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.id;
                      }}
                      renderOption={(option) => (
                        <div>
                          <div style={{ fontWeight: 500 }}>{option.name}</div>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>{option.id}</div>
                        </div>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          required
                          label="AWS Hosted Zone ID"
                          margin="dense"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>

                  {/* Admin Role ARN - Autocomplete */}
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      forcePopupIcon
                      options={envOptions[idx]?.adminRoles || []}
                      value={envOptions[idx]?.adminRoles?.find(r => r.arn === env.admin_role_arn) || env.admin_role_arn || null}
                      onChange={(_, val) => {
                        const arn = typeof val === 'object' && val ? val.arn : (typeof val === 'string' ? val : '');
                        handleEnvFieldChange(idx, 'admin_role_arn', arn);
                      }}
                      onInputChange={(_, newValue) => {
                        // Only update if it's not matching an existing option (i.e., manual typing)
                        if (!envOptions[idx]?.adminRoles?.some(r => r.arn === newValue)) {
                          handleEnvFieldChange(idx, 'admin_role_arn', newValue);
                        }
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.arn;
                      }}
                      renderOption={(option) => (
                        <div>
                          <div style={{ fontWeight: 500 }}>{option.name}</div>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>{option.arn}</div>
                        </div>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          required
                          label="Admin Role ARN"
                          margin="dense"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>

                  {/* Global ACM ARN - Autocomplete */}
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      forcePopupIcon
                      options={envOptions[idx]?.globalAcmCerts || []}
                      value={envOptions[idx]?.globalAcmCerts?.find(c => c.arn === env.aws_global_acm_arn) || env.aws_global_acm_arn || null}
                      onChange={(_, val) => {
                        const arn = typeof val === 'object' && val ? val.arn : (typeof val === 'string' ? val : '');
                        handleEnvFieldChange(idx, 'aws_global_acm_arn', arn);
                      }}
                      onInputChange={(_, newValue) => {
                        // Only update if it's not matching an existing option (i.e., manual typing)
                        if (!envOptions[idx]?.globalAcmCerts?.some(c => c.arn === newValue)) {
                          handleEnvFieldChange(idx, 'aws_global_acm_arn', newValue);
                        }
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option.arn;
                      }}
                      renderOption={(option) => (
                        <div>
                          <div style={{ fontWeight: 500 }}>{option.domain}</div>
                          <div style={{ fontSize: '0.85em', color: '#666' }}>{option.arn}</div>
                        </div>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          required
                          label="Global ACM ARN"
                        margin="dense"
                        variant="outlined"
                        />
                      )}
                      />
                    </Grid>

                  {/* AUTH0 Configuration Section */}
                  <Grid item xs={12}>
                    <Divider style={{ margin: '16px 0 8px' }} />
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" style={{ flexGrow: 1 }}>
                        AUTH0 Configuration (Optional)
                      </Typography>
                      <TextField
                        select
                        size="small"
                        value={env.auth0_enabled ? 'true' : 'false'}
                        onChange={(e) => handleEnvFieldChange(idx, 'auth0_enabled', e.target.value === 'true')}
                        style={{ minWidth: 100 }}
                      >
                        <MenuItem value="false">Disabled</MenuItem>
                        <MenuItem value="true">Enabled</MenuItem>
                      </TextField>
                    </Box>
                  </Grid>

                  {env.auth0_enabled && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="AUTH0 Client ID"
                          value={env.auth0_client_id || ''}
                          onChange={e => handleEnvFieldChange(idx, 'auth0_client_id', e.target.value)}
                          margin="dense"
                          variant="outlined"
                          placeholder="e.g., abc123xyz456"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="AUTH0 Domain"
                          value={env.auth0_domain || ''}
                          onChange={e => handleEnvFieldChange(idx, 'auth0_domain', e.target.value)}
                          margin="dense"
                          variant="outlined"
                          placeholder="e.g., your-tenant.auth0.com"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="password"
                          label="AUTH0 Client Secret"
                          value={env.auth0_client_secret || ''}
                          onChange={e => handleEnvFieldChange(idx, 'auth0_client_secret', e.target.value)}
                          margin="dense"
                          variant="outlined"
                          placeholder="Enter client secret"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="AUTH0 Scope"
                          value={env.auth0_scope || ''}
                          onChange={e => handleEnvFieldChange(idx, 'auth0_scope', e.target.value)}
                          margin="dense"
                          variant="outlined"
                          placeholder="e.g., openid profile email"
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider style={{ margin: '16px 0 8px' }} />
                    <Typography variant="subtitle2" gutterBottom>
                      AWS Regions Configuration
                    </Typography>
                    <Autocomplete
                      multiple
                      forcePopupIcon
                      options={AWS_REGIONS}
                      value={env.allowed_aws_regions}
                      onChange={(_, val) => handleAllowedRegionsChange(idx, val as string[])}
                      renderInput={params => {
                        const inputProps = { ...params.inputProps, required: false };
                        return (
                          <TextField
                            {...params}
                            label="Allowed AWS Regions"
                            variant="outlined"
                            margin="dense"
                            onBlur={() =>
                              setTouchedFields({
                                ...touchedFields,
                                [`${idx}-allowed_aws_regions`]: true,
                              })
                            }
                            InputProps={params.InputProps}
                            inputProps={inputProps}
                            error={
                              touchedFields[`${idx}-allowed_aws_regions`] &&
                              env.allowed_aws_regions.length === 0
                            }
                            helperText={
                              touchedFields[`${idx}-allowed_aws_regions`] &&
                              env.allowed_aws_regions.length === 0
                                ? 'Please select at least one region'
                                : undefined
                            }
                          />
                        );
                      }}
                    />
                  </Grid>

                  {env.allowed_aws_regions.length > 0 && (
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        required
                        label="Default AWS Region"
                        value={env.default_aws_region}
                        onChange={e => handleDefaultRegionChange(idx, e.target.value)}
                        onBlur={() =>
                          setTouchedFields({
                            ...touchedFields,
                            [`${idx}-default_aws_region`]: true,
                          })
                        }
                        error={
                          touchedFields[`${idx}-default_aws_region`] &&
                          !env.default_aws_region
                        }
                        helperText={
                          touchedFields[`${idx}-default_aws_region`] &&
                          !env.default_aws_region
                            ? 'Please select a default region'
                            : undefined
                        }
                      >
                        {env.allowed_aws_regions.map(r => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {env.regions.length > 0 && (
                    <Grid item xs={12}>
                      <Divider style={{ margin: '16px 0 8px' }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Region Specific Settings
                      </Typography>
                      {env.regions.map((r, ridx) => (
                        <Box
                          key={ridx}
                          mb={2}
                          p={1.5}
                          bgcolor="rgba(0,0,0,0.03)"
                          borderRadius={1}
                        >
                          <Typography
                            variant="body2"
                            style={{ marginBottom: 8, fontWeight: 500 }}
                          >
                            {r.region} ({r.region_short_name})
                          </Typography>
                          <Grid container spacing={2}>
                            {/* VPC ID Autocomplete */}
                            <Grid item xs={12}>
                              <Autocomplete
                                freeSolo
                                forcePopupIcon
                                loading={regionLoading[`${idx}-${r.region}`]}
                                options={regionOptions[idx]?.[r.region]?.vpcs || []}
                                value={r.aws_vpc_id}
                                onChange={(_, val) => {
                                  const vpcId = typeof val === 'object' && val ? val.id : (val || '');
                                  handleRegionFieldChange(idx, ridx, 'aws_vpc_id', vpcId);
                                }}
                                onInputChange={(_, newValue) => {
                                  // Only update if it's not matching an existing option (i.e., manual typing)
                                  if (!regionOptions[idx]?.[r.region]?.vpcs?.some(v => v.id === newValue)) {
                                    handleRegionFieldChange(idx, ridx, 'aws_vpc_id', newValue);
                                  }
                                }}
                                getOptionLabel={(option) => {
                                  if (typeof option === 'string') return option;
                                  return option.id;
                                }}
                                renderOption={(option) => (
                                  <div>
                                    <div style={{ fontWeight: 500 }}>{option.name}</div>
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                      {option.id} {option.cidr && `(${option.cidr})`}
                                    </div>
                                  </div>
                                )}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    required
                                    size="small"
                                    label="AWS VPC ID"
                                    margin="dense"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>
                            
                            {/* TFState Bucket Autocomplete */}
                            <Grid item xs={12}>
                              <Autocomplete
                                freeSolo
                                forcePopupIcon
                                loading={regionLoading[`${idx}-${r.region}`]}
                                options={regionOptions[idx]?.[r.region]?.buckets?.map(b => b.name) || []}
                                value={r.tfstate_bucket}
                                onChange={(_, val) => {
                                  // Handle both selection and manual entry (on blur/enter)
                                  handleRegionFieldChange(idx, ridx, 'tfstate_bucket', typeof val === 'string' ? val : '');
                                }}
                                onInputChange={(_, newValue, reason) => {
                                  // Only update on blur or clear, not on every keystroke
                                  if (reason === 'reset' || reason === 'clear') {
                                    handleRegionFieldChange(idx, ridx, 'tfstate_bucket', newValue);
                                  }
                                }}
                                onBlur={(e) => {
                                  // Update on blur to capture manual typing
                                  const target = e.target as HTMLInputElement;
                                  if (target.value !== r.tfstate_bucket) {
                                    handleRegionFieldChange(idx, ridx, 'tfstate_bucket', target.value);
                                  }
                                }}
                                renderInput={params => (
                                <TextField
                                    {...params}
                                  required
                                  size="small"
                                    label="TFSTATE BUCKET"
                                    margin="dense"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>
                            
                            {/* ACM ARN Autocomplete */}
                            <Grid item xs={12}>
                              <Autocomplete
                                freeSolo
                                forcePopupIcon
                                loading={regionLoading[`${idx}-${r.region}`]}
                                options={regionOptions[idx]?.[r.region]?.regionalAcmCerts || []}
                                value={regionOptions[idx]?.[r.region]?.regionalAcmCerts?.find(c => c.arn === r.aws_acm_arn) || r.aws_acm_arn || null}
                                onChange={(_, val) => {
                                  const arn = typeof val === 'object' && val ? val.arn : (typeof val === 'string' ? val : '');
                                  handleRegionFieldChange(idx, ridx, 'aws_acm_arn', arn);
                                }}
                                onInputChange={(_, newValue) => {
                                  // Only update if it's not matching an existing option (i.e., manual typing)
                                  if (!regionOptions[idx]?.[r.region]?.regionalAcmCerts?.some(c => c.arn === newValue)) {
                                    handleRegionFieldChange(idx, ridx, 'aws_acm_arn', newValue);
                                  }
                                }}
                                getOptionLabel={(option) => {
                                  if (typeof option === 'string') return option;
                                  return option.arn;
                                }}
                                renderOption={(option) => (
                                  <div>
                                    <div style={{ fontWeight: 500 }}>{option.domain}</div>
                                    <div style={{ fontSize: '0.85em', color: '#666' }}>{option.arn}</div>
                                  </div>
                                )}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    required
                                    size="small"
                                    label="AWS ACM ARN"
                                    margin="dense"
                                    variant="outlined"
                                  />
                                )}
                              />
                            </Grid>
                            
                            {/* Logging Bucket Autocomplete */}
                            <Grid item xs={12}>
                              <Autocomplete
                                freeSolo
                                forcePopupIcon
                                loading={regionLoading[`${idx}-${r.region}`]}
                                options={regionOptions[idx]?.[r.region]?.buckets?.map(b => b.name) || []}
                                value={r.logging_bucket}
                                onChange={(_, val) => {
                                  // Handle both selection and manual entry (on blur/enter)
                                  handleRegionFieldChange(idx, ridx, 'logging_bucket', typeof val === 'string' ? val : '');
                                }}
                                onInputChange={(_, newValue, reason) => {
                                  // Only update on blur or clear, not on every keystroke
                                  if (reason === 'reset' || reason === 'clear') {
                                    handleRegionFieldChange(idx, ridx, 'logging_bucket', newValue);
                                  }
                                }}
                                onBlur={(e) => {
                                  // Update on blur to capture manual typing
                                  const target = e.target as HTMLInputElement;
                                  if (target.value !== r.logging_bucket) {
                                    handleRegionFieldChange(idx, ridx, 'logging_bucket', target.value);
                                  }
                                }}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    required
                                    size="small"
                                    label="LOGGING BUCKET"
                                  margin="dense"
                                  variant="outlined"
                                  />
                                )}
                                />
                              </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Paper>
            );
          })}

          <Box display="flex" mt={2} mb={4}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddEnvironment}
            >
              Add Environment
            </Button>
          </Box>
        </>
      )}
    </FormControl>
  );
};